import React, { forwardRef, useMemo } from 'react';

interface ZReportTicketProps {
  register: any; // Full register including sales and payments
}

export const ZReportTicket = forwardRef<HTMLDivElement, ZReportTicketProps>(({ register }, ref) => {
  if (!register || !register.id) return null;

  const formatCurrency = (val: any) => `$${Number(val || 0).toFixed(2)}`;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    day: 'numeric', month: 'numeric', year: 'numeric'
  }) + ', ' + new Date(d).toLocaleTimeString('es-MX', {
    hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true
  }).toLowerCase();

  // Calculate payment method breakdown
  const breakdown = useMemo(() => {
    const totals: Record<string, number> = {
      CASH: 0,
      CARD: 0,
      TRANSFER: 0,
      VOUCHER: 0,
      CREDIT: 0,
      NOTA_CREDITO: 0,
    };

    if (register.sales && Array.isArray(register.sales)) {
      register.sales.forEach((sale: any) => {
        if (sale.payments && Array.isArray(sale.payments)) {
          sale.payments.forEach((payment: any) => {
            if (totals[payment.method] !== undefined) {
              totals[payment.method] += Number(payment.amount);
            }
          });
        }
      });
    }
    return totals;
  }, [register.sales]);

  const expectedCash = register.expectedCash ?? (Number(register.initialCash) + (breakdown.CASH || 0) + Number(register.totalCashIn) - Number(register.totalCashOut));
  const countedCash = register.countedCash ?? 0;
  const difference = register.difference ?? (countedCash - expectedCash);



  const padRight = (str: string, length: number) => {
    return str + ' '.repeat(Math.max(0, length - str.length));
  };
  
  const padLeft = (str: string, length: number) => {
    return ' '.repeat(Math.max(0, length - str.length)) + str;
  };

  const renderRow = (label: string, value: string) => {
    const totalLength = 32;
    const labelLen = label.length;
    const valLen = value.length;
    const spaces = Math.max(1, totalLength - labelLen - valLen);
    return `${label}${' '.repeat(spaces)}${value}`;
  };

  const isDiffPositive = difference >= 0;

  return (
    <div className="hidden print:block font-mono text-[12px] leading-[1.2] text-black bg-white" ref={ref}>
      <div className="w-[80mm] p-2 mx-auto">
        <div className="text-center mb-2">
          <h1 className="text-base font-bold mb-1">CORTE Z</h1>
          {register.branch?.company?.name ? (
            <p className="font-bold">{register.branch.company.name.toUpperCase()}</p>
          ) : (
            <p className="font-bold">POS EMPRESARIAL</p>
          )}
        </div>
        
        <div className="whitespace-pre">
          {'-'.repeat(32)}
          {'\n'}
          {renderRow('Sesión:', `#${register.id.slice(-4).toUpperCase()}`)}
          {'\n'}
          {renderRow('Caja:', register.branch?.name || 'Caja 1')}
          {'\n'}
          {renderRow('Cajero:', register.openedBy?.fullName || 'N/A')}
          {'\n'}
          {renderRow('Apertura:', register.openedAt ? formatDate(register.openedAt) : 'N/A')}
          {'\n'}
          {renderRow('Cierre:', register.closedAt ? formatDate(register.closedAt) : 'N/A')}
          {'\n'}
          {'-'.repeat(32)}
          {'\n'}
          <div className="text-center font-bold">MOVIMIENTOS DE CAJA</div>
          {'\n'}
          {renderRow('Monto Apertura:', formatCurrency(register.initialCash))}
          {'\n'}
          {renderRow(`Total Ventas (${register.saleCount || register._count?.sales || register.sales?.length || 0}):`, formatCurrency(register.totalSales))}
          {'\n'}
          {renderRow('Total Retiros:', `-${formatCurrency(register.totalCashOut)}`)}
          {'\n'}
          {'-'.repeat(32)}
          {'\n'}
          <div className="text-center font-bold">DESGLOSE POR MÉTODO</div>
          {'\n'}
          {renderRow('Efectivo:', formatCurrency(breakdown.CASH))}
          {'\n'}
          {renderRow('Tarjeta:', formatCurrency(breakdown.CARD))}
          {'\n'}
          {renderRow('Transferencia:', formatCurrency(breakdown.TRANSFER))}
          {'\n'}
          {renderRow('Vales:', formatCurrency(breakdown.VOUCHER))}
          {'\n'}
          {renderRow('Crédito:', formatCurrency(breakdown.CREDIT))}
          {'\n'}
          {renderRow('Nota de Crédito:', formatCurrency(breakdown.NOTA_CREDITO))}
          {'\n'}
          {'-'.repeat(32)}
          {'\n'}
          <div className="font-bold">
            {renderRow('MONTO CIERRE:', formatCurrency(countedCash))}
          </div>
          <div className={`font-bold ${isDiffPositive ? 'text-green-600 print:text-black' : 'text-red-600 print:text-black'}`}>
            {renderRow('DIFERENCIA:', formatCurrency(difference))}
          </div>
          {'-'.repeat(32)}
          {'\n'}
        </div>

        {register.note && (
          <div className="mt-2 mb-4">
            <p className="font-bold">Notas:</p>
            <p className="break-words">{register.note}</p>
          </div>
        )}

        <div className="mt-8 pt-4 text-center">
          <p>Firma: ____________________</p>
        </div>
      </div>
    </div>
  );
});
